import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

// Manual on-demand translation for news articles. The caller fires
// `run()` when the user taps the Translate button — auto-firing was
// considered but burned Gemini tokens on articles the user never
// actually read. The cached `article.translations[targetLang]` (if
// present) is surfaced synchronously so a previously-translated
// article shows the translation immediately without a tap.
//
// Returns `needed: true` when the UI language differs from the
// article language AND the body has been extracted — i.e. the
// translate button is meaningful to show. The component owns the
// trigger; this hook only knows when offering is appropriate.
export function useArticleTranslation(article, targetLang) {
  const articleId = article?.id || null
  const sourceLang = article?.language || null
  const hasContent = !!article?.full_content
  const cached = article?.translations?.[targetLang] || null

  const needed = !!(
    articleId && targetLang && sourceLang && sourceLang !== targetLang && hasContent
  )

  const [translation, setTranslation] = useState(cached)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Reset when the article or target language changes.
  useEffect(() => {
    setTranslation(cached)
    setError(null)
  }, [articleId, targetLang, cached])

  const run = useCallback(async ({ force = false } = {}) => {
    if (!articleId || !targetLang) return
    setLoading(true)
    setError(null)
    try {
      const response = await supabase.functions.invoke('translate-article', {
        body: { article_id: articleId, target_lang: targetLang, force },
      })
      if (response.error) {
        let detail = response.error.message || 'translation failed'
        const inner = response.error.context
        if (inner && typeof inner.text === 'function') {
          try {
            const raw = await inner.text()
            try {
              const parsed = JSON.parse(raw)
              if (parsed?.error) detail = parsed.error
              if (parsed?.detail) detail += ` — ${parsed.detail}`
            } catch { /* not JSON, use raw */ }
          } catch { /* ignore */ }
        }
        setError(detail)
      } else if (response.data?.error) {
        setError(response.data.error)
      } else if (response.data?.title && response.data?.content) {
        setTranslation({
          title: response.data.title,
          content: response.data.content,
          translated_at: response.data.translated_at,
          model: response.data.model,
        })
      } else {
        setError('empty translation response')
      }
    } catch (e) {
      setError(e?.message || 'translation failed')
    } finally {
      setLoading(false)
    }
  }, [articleId, targetLang])

  return { translation, loading, error, run, needed }
}
